import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { hasPageAccess } from '@/lib/utils/permissions';
import { useToast } from '@/hooks/use-toast';

/**
 * Page Access Guard Hook
 * 
 * Protects pages by checking if the current user has permission to view them.
 * If the user doesn't have access, they are redirected to the home page with a toast notification.
 * 
 * @param pageId - The page identifier (e.g., 'library', 'templates', 'settings')
 * 
 * @example
 * ```typescript
 * export default function LibraryPage() {
 *   usePageAccessGuard('library');
 *   // ... rest of component
 * }
 * ```
 */
export function usePageAccessGuard(pageId: string) {
  const navigate = useNavigate();
  const { selectedCompany } = useSimplifiedUserData();
  const { toast } = useToast();

  useEffect(() => {
    // Skip check if no company is selected
    if (!selectedCompany?.id) return;

    // Check if user has access to this page
    const hasAccess = hasPageAccess(selectedCompany.id, pageId);
    
    if (!hasAccess) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to view this page.',
        variant: 'destructive',
      });
      navigate('/home', { replace: true });
    }
  }, [selectedCompany?.id, pageId, navigate, toast]);
}
